import { formatDateCourte } from './desktopVersions';

/** 12 chiffres avant la virgule, 2 après : la colonne NUMERIC(14,2) du serveur. */
export const MONTANT_CHIFFRES_MAX = 12;

/**
 * Montant saisi (« 15 000 », « 15000,5 », « 15 000.50 ») → texte décimal exact attendu par le
 * serveur (« 15000.50 »), ou null s'il n'est pas un montant positif valide. Les espaces sont
 * ignorés, la virgule vaut le point ; un seul séparateur décimal, deux décimales au plus. Un
 * texte ambigu comme « 15.000 » (trois décimales) ou « 1.000.000 » est refusé plutôt que deviné.
 */
export const normaliserMontant = (saisie: string): string | null => {
  const texte = saisie.replace(/\s/g, '').replace(',', '.');
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(texte);
  if (!match || match[1].length > MONTANT_CHIFFRES_MAX) {
    return null;
  }
  const entier = match[1].replace(/^0+(?=\d)/, '');
  const decimales = (match[2] || '').padEnd(2, '0');
  if (entier === '0' && decimales === '00') {
    return null;
  }
  return `${entier}.${decimales}`;
};

const centimes = (valeur: number): number => Math.round(valeur * 100);

/** 1234567.5 → « 1 234 567,50 DA » (espaces insécables) ; négatif précédé de « - ». */
export const formatMontant = (valeur: number, avecDevise = true): string => {
  const total = Math.abs(centimes(valeur));
  const entier = Math.floor(total / 100)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const decimales = String(total % 100).padStart(2, '0');
  const signe = centimes(valeur) < 0 ? '-' : '';
  return `${signe}${entier},${decimales}${avecDevise ? ' DA' : ''}`;
};

/** Somme exacte au centime (les montants reçus sont des flottants à 2 décimales). */
export const sommeMontants = (valeurs: number[]): number =>
  valeurs.reduce((total, valeur) => total + centimes(valeur), 0) / 100;

export type SensSolde = 'debiteur' | 'crediteur' | 'solde';

/** Débiteur : le client doit ; créditeur : il a versé plus que ses créances. */
export const sensSolde = (solde: number): SensSolde => {
  const valeur = centimes(solde);
  if (valeur > 0) {
    return 'debiteur';
  }
  return valeur < 0 ? 'crediteur' : 'solde';
};

export const libelleSolde = (solde: number): string => {
  switch (sensSolde(solde)) {
    case 'debiteur':
      return `Doit ${formatMontant(solde)}`;
    case 'crediteur':
      return `Créditeur de ${formatMontant(-solde)}`;
    default:
      return 'Soldé';
  }
};

export type ClasseAnciennete = 'recente' | 'moyenne' | 'ancienne';

/** Plus ancienne créance impayée : jusqu'à 30 jours, jusqu'à 90 jours, au-delà. */
export const classeAnciennete = (jours: number): ClasseAnciennete => {
  if (jours > 90) {
    return 'ancienne';
  }
  return jours > 30 ? 'moyenne' : 'recente';
};

export const libelleAnciennete = (jours: number): string => {
  if (jours < 0) {
    return `dans ${-jours} j`;
  }
  return jours === 0 ? "aujourd'hui" : `${jours} j`;
};

/** Période d'un relevé, en toutes lettres. */
export const libellePeriode = (du?: string | null, au?: string | null): string => {
  if (du && au) {
    return `du ${formatDateCourte(du)} au ${formatDateCourte(au)}`;
  }
  if (du) {
    return `depuis le ${formatDateCourte(du)}`;
  }
  if (au) {
    return `jusqu'au ${formatDateCourte(au)}`;
  }
  return 'tous les mouvements';
};
